import React, { Component } from 'react';
import propTypes from 'prop-types';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import { EditorState, Modifier } from 'draft-js';
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';

import sharedStyles from 'app/shared.css';
import { noop } from 'lib/noop';
import { removeHTML } from 'lib/string';
import { makeId } from 'utils/selectors';

import styles from './LiveTextEditor.css';
import LiveTextLinkButton from './LiveTextLinkButton';
import HelpIcon from './HelpIcon';

/**
 * a live text editor
 */
export default class LiveTextEditor extends Component {
  static propTypes = {
    fieldContract: propTypes.shape({
      name: propTypes.string,
      required: propTypes.bool,
      helpText: propTypes.string
    }).isRequired,
    isReadonly: propTypes.bool,
    onChange: propTypes.func.isRequired,
    selectedValue: propTypes.string,
    updateLocation: propTypes.string.isRequired,
    validationMessage: propTypes.string
  };

  static defaultProps = {
    isReadonly: false,
    selectedValue: '',
    validationMessage: undefined
  };

  static CONFIG = {
    options: ['inline', 'list', 'remove', 'history', 'blockType'],
    inline: { options: ['bold', 'italic', 'strikethrough'] },
    link: { showOpenOptionOnHover: false },
    list: { options: ['unordered', 'ordered'] },
    blockType: { options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'] }
  };

  constructor(props) {
    super(props);

    // convert existing/default html value to EditorState
    const { selectedValue: html = '' } = props;
    const editorState = html
      ? EditorState.createWithContent(stateFromHTML(html))
      : EditorState.createEmpty();

    this.state = {
      editorState,
      isFocused: false
    };

    this.handlePastedText = this.handlePastedText.bind(this);
    this.handleRef = this.handleRef.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onEditorStateChange = this.onEditorStateChange.bind(this);
  }

  /**
   * paste as plain text
   * @param {string} text - paste content as plain text
   * @param {string} html - paste content as html
   * @param {EditorState} editorState - editor state
   * @param {Function} onChange - function to update editor
   * @returns {boolean} whether text was pasted
   */
  handlePastedText(text, html, editorState, onChange) {
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      text
    );
    onChange(EditorState.push(editorState, contentState, 'insert-characters'));
    return true;
  }

  /**
   * assign editor to instance and add tate-test-id attribute
   * this is necessary because the component we're using doesn't pass props through
   * @param {object} ref - draftjs editor ref
   */
  handleRef(ref) {
    if (ref && ref.editor) {
      if (this.editorRef !== ref.editor) {
        this.editorRef = ref.editor;
      }

      if (!this.editorRef.hasAttribute('data-test-id')) {
        const { updateLocation } = this.props;
        this.editorRef.setAttribute('data-test-id', makeId({ updateLocation }));
      }
    }
  }

  /**
   * convert editor content to html and update redux store
   */
  onChange() {
    const { editorState } = this.state;
    const html = stateToHTML(editorState.getCurrentContent());

    // only use html with actual content
    this.props.onChange(removeHTML(html) ? html : '');
  }

  /**
   * update `editorState` state
   * @param {EditorState} editorState - new editor state
   */
  onEditorStateChange(editorState) {
    const isFocused = document.activeElement === this.editorRef;
    this.setState({ editorState, isFocused }, !isFocused ? this.onChange : noop);
  }

  render() {
    const {
      fieldContract: { name, required, helpText },
      isReadonly,
      updateLocation,
      validationMessage
    } = this.props;
    const { editorState, isFocused } = this.state;

    return (
      <div className="d-flex flex-column my-3">
        <span className="d-flex align-items-center font-weight-bold mb-2">
          {`${name}${required ? '*' : ''}`}
          {helpText && <HelpIcon id={makeId({ updateLocation })} message={helpText} />}
        </span>
        <WysiwygEditor
          editorClassName={`border-top border-light px-3 ${styles.editor}`}
          toolbarClassName={`border-0 ${!isFocused ? styles.disabledToolbar : ''}`}
          wrapperClassName="border border-secondary"
          editorRef={this.handleRef}
          editorState={editorState}
          handlePastedText={this.handlePastedText}
          readOnly={isReadonly}
          spellCheck
          toolbar={LiveTextEditor.CONFIG}
          toolbarCustomButtons={[
            <LiveTextLinkButton id={makeId({ updateLocation, name: 'link' })} />
          ]}
          onEditorStateChange={this.onEditorStateChange}
        />
        <span className={sharedStyles.fieldValidationMessage}>
          {Array.isArray(validationMessage) ? validationMessage.join('\n') : validationMessage}
        </span>
      </div>
    );
  }
}
